import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, IconButton, Modal, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useTranslation } from "react-i18next";

type PaymentMethod = "card" | "wallet";

interface BridgerPayWidgetProps {
  cashierKey: string;
  cashierToken: string;
  externalId: string;
  open?: boolean;
  useModal?: boolean;
  /** If provided, the in-widget Card/Apple Pay selector is skipped and the
   *  launcher is mounted directly in that mode. Used when the parent has
   *  already routed the user (e.g. APPLE_PAY option chosen upstream). */
  defaultMethod?: PaymentMethod;
  onClose: () => void;
  onSuccess?: (externalId: string) => void;
  /** Fired once on ANY terminal deposit outcome (approved, declined, error…),
   *  unlike onSuccess which only fires on approval. Used to route the user to
   *  the payment-status page in every case, not only on success. */
  onResult?: (externalId: string, status?: string) => void;
  onTokenReceived?: (token: string, externalId: string) => void;
}

const BP_LAUNCHER_SRC = "https://checkout.bridgerpay.com/v2/launcher";

const BridgerPayWidget: React.FC<BridgerPayWidgetProps> = ({
  cashierKey,
  cashierToken,
  externalId,
  open = true,
  useModal = false,
  defaultMethod,
  onClose,
  onSuccess,
  onResult,
  onTokenReceived,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("paymentModal");

  const walletContainerRef = useRef<HTMLDivElement | null>(null);
  const checkoutContainerRef = useRef<HTMLDivElement | null>(null);
  const successFiredRef = useRef(false);
  const resultFiredRef = useRef(false);

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    defaultMethod ?? null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleWalletReady = useCallback(() => {
    setLoading(false);
    const applePayButton = document.querySelector(".wallet-apple-pay") as HTMLElement | null;
    if (applePayButton) {
      applePayButton.style.setProperty("-apple-pay-button-type", "buy", "important");
      applePayButton.style.setProperty("-apple-pay-button-style", "black", "important");
    }
  }, []);

  // Two launcher modes, mounted depending on the user's selection:
  //   - selectedMethod === null  → wallet launcher mounted in the selection
  //                                 screen so BridgerPay renders the native
  //                                 Apple Pay button directly. Clicking that
  //                                 button opens the Apple Pay overlay (no
  //                                 intermediate "click again" step).
  //   - selectedMethod === "card" → standard checkout launcher (card form).
  useEffect(() => {
    if (!open) return;

    if (!cashierKey || !cashierToken) {
      setError(t("BRIDGERPAY_WIDGET.INCOMPLETE_PAYMENT_INFORMATION", "Incomplete payment information"));
      return;
    }

    // Wallet launcher is mounted both when the user is on the selection screen
    // (selectedMethod === null) — so the BridgerPay Apple Pay button shows
    // inline — and when the parent passed defaultMethod="wallet" directly
    // (selectedMethod === "wallet"). The card launcher is mounted only for
    // selectedMethod === "card".
    const isWalletMode = selectedMethod !== "card";
    const targetContainer = isWalletMode
      ? walletContainerRef.current
      : checkoutContainerRef.current;
    if (!targetContainer) return;

    setError(null);
    setLoading(true);
    successFiredRef.current = false;
    resultFiredRef.current = false;

    // Hide Google Pay — we only want the Apple Pay button. Per the BridgerPay
    // docs, BRIDGERPAY_CONFIG must be set BEFORE the launcher initializes.
    if (isWalletMode) {
      const w = window as unknown as { BRIDGERPAY_CONFIG?: { hideWallets?: string[] } };
      w.BRIDGERPAY_CONFIG = { ...(w.BRIDGERPAY_CONFIG ?? {}), hideWallets: ["GooglePay"] };
    }

    const script = document.createElement("script");
    script.src = BP_LAUNCHER_SRC;
    script.setAttribute("data-cashier-key", cashierKey);
    script.setAttribute("data-cashier-token", cashierToken);
    if (isWalletMode) {
      script.setAttribute("data-button-mode", "wallet");
    } else {
      script.setAttribute("data-theme", "bright");
    }
    script.async = true;

    const handleLoad = () => {
      setTimeout(() => setLoading(false), 500);
    };
    const handleError = () => {
      setError(t("BRIDGERPAY_WIDGET.LOAD_ERROR", "Unable to load payment widget"));
      setLoading(false);
    };

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);

    targetContainer.appendChild(script);

    const loadingTimeout = setTimeout(() => setLoading(false), 15000);

    return () => {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
      if (script.parentNode) script.parentNode.removeChild(script);
      clearTimeout(loadingTimeout);
      walletContainerRef.current?.replaceChildren();
      checkoutContainerRef.current?.replaceChildren();
    };
  }, [open, cashierKey, cashierToken, t, selectedMethod]);

  // BridgerPay events are dispatched as CustomEvents on the parent window now
  // that the launcher runs same-origin. Two distinct prefixes per the docs:
  //   - "[bp][cashier:KEY]:..."  for cashier-level events (deposit, redirect…)
  //   - "[bp][checkout:KEY]:..." for wallet events (Apple Pay / Google Pay)
  // The legacy unkeyed form "[bp]:..." is also listened to as a safety net.
  useEffect(() => {
    if (!open || !cashierKey) return;

    const cashierPrefix = `[bp][cashier:${cashierKey}]`;
    const checkoutPrefix = `[bp][checkout:${cashierKey}]`;
    const legacyPrefix = "[bp]";

    const onDeposit = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.type === "approved" && !successFiredRef.current) {
        successFiredRef.current = true;
        onSuccess?.(externalId);
      }
      // Any terminal deposit outcome (approved / declined / error) → notify once
      // so the parent can route to the payment-status page in every case.
      if (!resultFiredRef.current) {
        resultFiredRef.current = true;
        onResult?.(externalId, detail?.type);
      }
    };

    const onPaymentCardToken = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!successFiredRef.current && detail?.token) {
        successFiredRef.current = true;
        onTokenReceived?.(detail.token, externalId);
      }
    };

    const onRedirect = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.url) window.location.href = detail.url;
    };

    const onCloseEvent = () => onClose();

    // Apple Pay / Google Pay: success path. The cashier "deposit" event
    // (type=approved) also fires for wallet payments, but `wallet-result` is
    // emitted earlier and carries the wallet-specific outcome.
    const onWalletResult = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const approved = detail?.type === "approved" || detail?.status === "approved" || detail?.success === true;
      if (approved && !successFiredRef.current) {
        successFiredRef.current = true;
        onSuccess?.(externalId);
      }
      // Terminal wallet outcome (approved or not) → notify once for routing.
      if (!resultFiredRef.current) {
        resultFiredRef.current = true;
        onResult?.(externalId, detail?.type ?? detail?.status);
      }
    };

    // User dismissed the Apple Pay / Google Pay native overlay without paying.
    // Make sure the loader doesn't stay stuck.
    const onWalletCancelled = () => {
      setLoading(false);
    };

    const onNoWalletProvider = () => {
      setError(
        t(
          "BRIDGERPAY_WIDGET.NO_WALLET_PROVIDER",
          "No wallet provider available for this payment method"
        )
      );
      setLoading(false);
    };

    const onWalletProviderCannotBeUsed = () => {
      setError(
        t(
          "BRIDGERPAY_WIDGET.WALLET_NOT_SUPPORTED",
          "This wallet is not supported on your device or browser"
        )
      );
      setLoading(false);
    };

    const events: Array<[string, EventListener]> = [
      // Cashier-level events
      [`${cashierPrefix}:deposit`, onDeposit],
      [`${legacyPrefix}:deposit`, onDeposit],
      [`${cashierPrefix}:paymentCardToken`, onPaymentCardToken],
      [`${legacyPrefix}:paymentCardToken`, onPaymentCardToken],
      [`${cashierPrefix}:redirect`, onRedirect],
      [`${legacyPrefix}:redirect`, onRedirect],
      [`${cashierPrefix}:close`, onCloseEvent],
      [`${legacyPrefix}:close`, onCloseEvent],
      // Wallet events (Apple Pay / Google Pay) — checkout: prefix per docs
      [`${checkoutPrefix}:wallet-button-is-ready`, handleWalletReady],
      [`${checkoutPrefix}:wallet-result`, onWalletResult],
      [`${checkoutPrefix}:wallet-cancelled`, onWalletCancelled],
      [`${checkoutPrefix}:no-wallet-provider-found`, onNoWalletProvider],
      [`${checkoutPrefix}:wallet-provider-cannot-be-used`, onWalletProviderCannotBeUsed],
    ];

    events.forEach(([name, listener]) => window.addEventListener(name, listener));

    return () => {
      events.forEach(([name, listener]) => window.removeEventListener(name, listener));
    };
  }, [open, cashierKey, externalId, onSuccess, onResult, onTokenReceived, onClose, handleWalletReady, t]);

  if (!open) return null;

  const WidgetContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "min(560px, 80vh)",
          bgcolor: "#ffffff",
          borderRadius: 3,
          boxShadow: useModal ? theme.shadows[24] : theme.shadows[3],
          overflow: "hidden",
          // BridgerPay's launcher renders its UI with position:fixed. Setting a
          // transform on this wrapper creates a containing block so the fixed
          // children stay scoped to the 560px frame instead of escaping to the
          // viewport (previously this was naturally handled by the iframe).
          transform: "translateZ(0)",
          willChange: "transform",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            p: 1.5,
          }}
        >
          {selectedMethod === null && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                px: 3,
              }}
            >
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t("BRIDGERPAY_WIDGET.CHOOSE_METHOD", "Choose your payment method")}
              </Typography>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<CreditCardIcon />}
                onClick={() => setSelectedMethod("card")}
                sx={{ maxWidth: 360, py: 1.5 }}
              >
                {t("BRIDGERPAY_WIDGET.CREDIT_CARD", "Credit / Debit Card")}
              </Button>

              {/* BridgerPay-rendered Apple Pay button — clicking it opens the
                  native Apple Pay overlay directly (no intermediate step). */}
              <Box
                ref={walletContainerRef}
                id="bp-wallet-container"
                sx={{
                  width: "100%",
                  maxWidth: 360,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  "& .wallet-apple-pay, & .wallet-google-pay": {
                    width: "100%",
                    minHeight: 48,
                  },
                }}
              />
            </Box>
          )}

          {selectedMethod === "card" && (
            <>
              {!defaultMethod && (
                <Button
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => {
                    setSelectedMethod(null);
                    setError(null);
                  }}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {t("BRIDGERPAY_WIDGET.CHANGE_METHOD", "Change method")}
                </Button>
              )}

              <Box
                ref={checkoutContainerRef}
                id="bp-checkout-container"
                sx={{ width: "100%", flex: 1, minHeight: 0 }}
              />
            </>
          )}

          {selectedMethod === "wallet" && (
            <>
              {!defaultMethod && (
                <Button
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => {
                    setSelectedMethod(null);
                    setError(null);
                  }}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {t("BRIDGERPAY_WIDGET.CHANGE_METHOD", "Change method")}
                </Button>
              )}

              <Box
                ref={walletContainerRef}
                id="bp-wallet-container"
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  flex: 1,
                  minHeight: 0,
                  "& .wallet-apple-pay, & .wallet-google-pay": {
                    width: "100%",
                    maxWidth: 360,
                    minHeight: 48,
                  },
                }}
              />
            </>
          )}
        </Box>

        {loading && !error && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(255,255,255,0.8)",
              zIndex: 2,
            }}
          >
            <CircularProgress size={60} />
          </Box>
        )}

        {error && (
          <Box sx={{ position: "absolute", inset: 0, p: 2, zIndex: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
      </Box>

      {/* Open in new tab fallback for 3DS issues */}
      {!loading && !error && cashierKey && cashierToken && selectedMethod !== null && (
        <Box sx={{ mt: 1, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary">
            {t("BRIDGERPAY_WIDGET.DISPLAY_ISSUE", "Having trouble?")}
          </Typography>
          <Button
            variant="text"
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={() => window.open(`https://checkout.bridgerpay.com/?cashierKey=${encodeURIComponent(cashierKey)}&cashierToken=${encodeURIComponent(cashierToken)}`, "_blank")}
            sx={{ ml: 1 }}
          >
            {t("BRIDGERPAY_WIDGET.OPEN_IN_BROWSER", "Open in new tab")}
          </Button>
        </Box>
      )}
    </Box>
  );

  if (useModal) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        closeAfterTransition
        slots={{ backdrop: "div" }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: "rgba(0,0,0,0.7)",
              zIndex: theme.zIndex.modal + 1,
            },
          },
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: theme.zIndex.modal + 2,
          }}
        >
          {WidgetContent}
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: "calc(50% - min(250px,40vh) - 18px)",
              right: "calc(50% - min(200px,40vw) - 18px)",
              bgcolor: "#A5A5A5",
              width: 36,
              height: 36,
              boxShadow: theme.shadows[3],
              "&:hover": { bgcolor: "#8c8c8c" },
              zIndex: theme.zIndex.modal + 3,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Modal>
    );
  }

  return WidgetContent;
};

export default BridgerPayWidget;
